import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "onExpandedChange", "compact", "defaultExpanded", "expanded", "expandable"];
import { forwardRef } from 'react';
import styled from 'foundations-theming/utils';
import { CardContextProvider } from './CardContext';
import { useDisclosure } from '../../../hooks/useDisclosure';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { StyledCardSection } from './CardSection';
import { StyledHeader } from './CardHeader';
import { inheritBorderRadiusStyles } from '../../utils/styles/styles';
import { jsx as _jsx } from "react/jsx-runtime";
const Outer = styled.div.withConfig({
  displayName: "CardWrapper__Outer",
  componentId: "e6ijbg-0"
})(({
  $cssStyles,
  theme
}) => {
  return Object.assign({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: 0,
    backgroundColor: theme.color['fill-surface-default'],
    boxShadow: theme.boxShadow['100'],
    height: 'inherit',
    border: `${theme.borderWidth['transitional-2']} solid ${theme.color['border-container-default']}`,
    marginBlockEnd: theme.space['400'],
    borderRadius: theme.borderRadius['transitional-container-alt']
  }, inheritBorderRadiusStyles(), {
    [`& ${StyledCardSection}`]: {
      '&:last-child': {
        marginBottom: 0
      }
    },
    /**
     * Targets any StyledHeader/StyledCardSection that is contained within
     * any element (*) that is an immediate next sibling to a previous StyledHeader
     * and applies paddingTop of 0 to it.
     */
    [`& ${StyledHeader} + * ${StyledHeader}`]: {
      paddingTop: theme.space['0']
    },
    [`& ${StyledHeader} + * ${StyledCardSection}`]: {
      paddingTop: theme.space['0']
    }
  }, $cssStyles);
});
export const CardWrapper = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
      children,
      onExpandedChange,
      compact = false,
      defaultExpanded = true,
      expanded = undefined,
      expandable = false
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const {
    isOpen,
    triggerElementAriaAttributes,
    onOpen,
    onClose
  } = useDisclosure({
    defaultIsOpen: defaultExpanded,
    isOpen: expanded,
    onOpen: () => {
      onExpandedChange === null || onExpandedChange === void 0 || onExpandedChange(true);
    },
    onClose: () => {
      onExpandedChange === null || onExpandedChange === void 0 || onExpandedChange(false);
    }
  });
  const isExpandable = expandable && (expanded !== undefined || defaultExpanded !== undefined);
  return /*#__PURE__*/_jsx(CardContextProvider, {
    expanded: isOpen,
    expandable: isExpandable,
    defaultExpanded: defaultExpanded,
    onOpen: onOpen,
    onClose: onClose,
    compact: compact,
    inCard: true,
    children: /*#__PURE__*/_jsx(Outer, Object.assign({}, domProps, triggerElementAriaAttributes, {
      ref: ref,
      $cssStyles: cssStyles,
      children: children
    }))
  });
});
CardWrapper.displayName = 'CardWrapper';